import React, { useState, createContext, useContext, ReactNode } from 'react';
import Confirmation from './confirmation';

interface ConfirmOptions {
  title?: string;
  okLabel?: string;
  cancelLabel?: string;
  showOk?: boolean;
}

interface ConfirmContextType {
  confirm: (message: string, options?: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export const ConfirmProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [confirmState, setConfirmState] = useState<{
    show: boolean;
    message: string;
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const confirm = (message: string, options: ConfirmOptions = {}): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState({
        show: true,
        message,
        options,
        resolve,
      });
    });
  };

  const handleProceed = () => {
    if (confirmState?.resolve) {
      confirmState.resolve(true);
    }
    setConfirmState(null);
  };

  const handleCancel = () => {
    if (confirmState?.resolve) {
      confirmState.resolve(false);
    }
    setConfirmState(null);
  };

  const handleDismiss = () => {
    if (confirmState?.resolve) {
      confirmState.resolve(false);
    }
    setConfirmState(null);
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {confirmState && (
        <Confirmation
          show={confirmState.show}
          confirmation={confirmState.message}
          title={confirmState.options.title}
          okLabel={confirmState.options.okLabel}
          cancelLabel={confirmState.options.cancelLabel}
          showOk={confirmState.options.showOk}
          proceed={handleProceed}
          cancel={handleCancel}
          dismiss={handleDismiss}
        />
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmContextType => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};
